/**
 * Edición inline de una celda de la tabla (§7).
 *
 * El PATCH sale con actualización optimista: la celda cambia al instante y
 * vuelve a su valor anterior si el servidor rechaza el cambio. Ese rollback lo
 * hace `useEditarStakeholder`, que también parchea la caché de la tabla.
 *
 * La fila entera abre el panel de detalle al pincharla, así que todos los
 * eventos de la celda paran su propagación.
 */

import { Check, Loader2, X } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { useEditarStakeholder } from '../../api/hooks'
import type { OpcionEnum, Stakeholder, StakeholderFila } from '../../api/tipos'
import { mensajeDeError, useAvisos } from '../../ui/avisos-contexto'
import { cx } from '../../ui/util'

interface Comun {
  fila: StakeholderFila
  entidadId: number
  campo: keyof StakeholderFila & string
  /** Lo que se muestra cuando no se está editando. */
  children: ReactNode
}

function useGuardar(entidadId: number, fila: StakeholderFila, campo: string) {
  const avisos = useAvisos()
  const editar = useEditarStakeholder(entidadId)

  return {
    pendiente: editar.isPending,
    guardar: (valor: unknown, alTerminar?: () => void) => {
      if (valor === fila[campo as keyof StakeholderFila]) {
        alTerminar?.()
        return
      }
      editar.mutate(
        { id: fila.id, cambios: { [campo]: valor } as Partial<Stakeholder> },
        {
          onSuccess: () => alTerminar?.(),
          onError: (error) => {
            avisos.error(
              `No se pudo cambiar «${campo.replace(/_/g, ' ')}»`,
              mensajeDeError(error),
            )
            alTerminar?.()
          },
        },
      )
    },
  }
}

/** Enum: se convierte en un desplegable al pinchar. */
export function CeldaEnum({
  fila,
  entidadId,
  campo,
  opciones,
  children,
}: Comun & { opciones: OpcionEnum[] }) {
  const [editando, setEditando] = useState(false)
  const { guardar, pendiente } = useGuardar(entidadId, fila, campo)

  if (!editando) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          setEditando(true)
        }}
        title="Pincha para cambiarlo"
        className="w-full rounded px-0.5 text-left hover:bg-[var(--color-acento-suave)]"
      >
        {children}
      </button>
    )
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <select
        autoFocus
        disabled={pendiente}
        defaultValue={String(fila[campo as keyof StakeholderFila] ?? '')}
        onChange={(e) => guardar(e.target.value, () => setEditando(false))}
        onBlur={() => setEditando(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditando(false)
        }}
        className="w-full rounded border border-[var(--color-acento)] bg-[var(--color-panel)] px-1 py-0.5 text-[12px]"
      >
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
      </select>
      {pendiente && <Loader2 size={12} className="animate-spin shrink-0" />}
    </span>
  )
}

/** Texto: se edita con doble clic, para no chocar con el clic de la fila. */
export function CeldaTexto({ fila, entidadId, campo, children }: Comun) {
  const [editando, setEditando] = useState(false)
  const [valor, setValor] = useState('')
  const entrada = useRef<HTMLInputElement>(null)
  const { guardar, pendiente } = useGuardar(entidadId, fila, campo)

  useEffect(() => {
    if (editando) entrada.current?.select()
  }, [editando])

  function abrir(e: React.MouseEvent) {
    e.stopPropagation()
    setValor(String(fila[campo as keyof StakeholderFila] ?? ''))
    setEditando(true)
  }

  if (!editando) {
    return (
      <span
        onDoubleClick={abrir}
        title="Doble clic para editar"
        className="block truncate rounded px-0.5 hover:bg-[var(--color-acento-suave)]"
      >
        {children}
      </span>
    )
  }

  const confirmar = () => guardar(valor.trim(), () => setEditando(false))

  return (
    <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={entrada}
        autoFocus
        disabled={pendiente}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirmar()
          if (e.key === 'Escape') setEditando(false)
        }}
        className="min-w-0 flex-1 rounded border border-[var(--color-acento)] bg-[var(--color-panel)] px-1 py-0.5 text-[13px]"
      />
      <button
        onClick={confirmar}
        disabled={pendiente}
        className="shrink-0 text-[var(--color-favorable)]"
        aria-label="Guardar"
      >
        {pendiente ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
      </button>
      <button
        onClick={() => setEditando(false)}
        className={cx('shrink-0 text-[var(--color-texto-tenue)]', pendiente && 'opacity-40')}
        aria-label="Cancelar"
      >
        <X size={13} />
      </button>
    </span>
  )
}
